import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { adminGetSettings, adminUpdateSettings, adminUpload, apiError } from "@/lib/api";
import { AdminHeader, PrimaryBtn, fieldCls, labelCls } from "./ui";

const EMPTY = { imagen: "", imagen_movil: "", enlace: "", alt: "" };
const fill = (list) => [0, 1, 2, 3].map((k) => ({ ...EMPTY, ...(list?.[k] || {}) }));

function ImageField({ label, spec, value, onChange, testId, ratio }) {
  const [up, setUp] = useState(false);
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUp(true);
    try { const res = await adminUpload(file); onChange(res.url); toast.success("Imagen subida."); }
    catch (err) { toast.error(apiError(err)); } finally { setUp(false); e.target.value = ""; }
  };
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <p className="text-xs text-[#4a4a45] mb-2"><strong>{spec}</strong> · JPG o PNG</p>
      <div className={`relative overflow-hidden rounded-lg bg-[#0a1b33] ${ratio} mb-2`}>
        {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-white/50 text-xs">Sin imagen</div>}
        {value && <button type="button" onClick={() => onChange("")} className="absolute top-2 right-2 grid place-items-center w-8 h-8 rounded-full bg-white/90 text-[#0f2544] hover:bg-white" aria-label="Quitar imagen" data-testid={`${testId}-remove`}><Trash2 size={14} /></button>}
      </div>
      <input type="file" accept="image/*" onChange={upload} className="text-sm" data-testid={`${testId}-input`} />
      {up && <span className="text-xs text-[#8a8a83] ml-2">Subiendo...</span>}
    </div>
  );
}

function BannerCard({ k, b, set }) {
  const upd = (field) => (v) => set(k, { ...b, [field]: v });
  return (
    <div className="rounded-2xl border border-[#0f2544]/10 bg-white p-5 sm:p-6 space-y-5" data-testid={`banner-card-${k}`}>
      <p className="font-serif text-xl font-semibold text-[#0f2544]">Banner {k + 1}</p>
      <div className="grid md:grid-cols-[2fr_1fr] gap-5">
        <ImageField label="Imagen para ordenador" spec="1920 × 800 px" ratio="aspect-[12/5]" value={b.imagen} onChange={upd("imagen")} testId={`banner-${k}-imagen`} />
        <ImageField label="Imagen para móvil (opcional)" spec="800 × 1000 px" ratio="aspect-[4/5] max-w-[180px]" value={b.imagen_movil} onChange={upd("imagen_movil")} testId={`banner-${k}-movil`} />
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Enlace al pulsar el banner</label>
          <input value={b.enlace} onChange={(e) => upd("enlace")(e.target.value)} className={fieldCls} placeholder="Ej: /libros/transformado o /ebooks" data-testid={`banner-${k}-enlace`} />
        </div>
        <div>
          <label className={labelCls}>Descripción breve (para Google y accesibilidad)</label>
          <input value={b.alt} onChange={(e) => upd("alt")(e.target.value)} className={fieldCls} placeholder="Ej: Nuevo libro Transformado" data-testid={`banner-${k}-alt`} />
        </div>
      </div>
    </div>
  );
}

export default function AdminHero() {
  const [banners, setBanners] = useState(fill([]));
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminGetSettings().then((s) => setBanners(fill(s.banners))).catch(() => {}); }, []);

  const set = (k, v) => setBanners((list) => list.map((x, j) => (j === k ? v : x)));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { const s = await adminUpdateSettings({ banners }); setBanners(fill(s.banners)); toast.success("Banners de portada guardados."); }
    catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <div>
      <AdminHeader title="Banners de portada" subtitle="Hasta 4 banners que pasan solos cada 6 segundos en la página de inicio." />
      <div className="rounded-lg bg-[#eef1ff] border border-[#1f44e0]/20 px-4 py-3 text-sm text-[#0f2544] mb-6 max-w-4xl" data-testid="banner-specs">
        <strong>Medidas:</strong> ordenador 1920 × 800 px · móvil 800 × 1000 px. Peso máximo 25 MB.<br />
        <span className="text-xs text-[#4a4a45]">Deja el texto importante lejos de la esquina inferior izquierda, donde van los números del carrusel. Los banners sin imagen de ordenador no se muestran. La imagen de móvil es opcional: si no la subís, en el teléfono se verá el banner de ordenador completo, sin recortes, en tamaño más pequeño. Si la subís en todos los banners, el móvil mostrará vuestra versión vertical a pantalla grande.</span>
      </div>
      <form onSubmit={save} className="space-y-6 max-w-4xl" data-testid="hero-form">
        {banners.map((b, k) => <BannerCard key={k} k={k} b={b} set={set} />)}
        <PrimaryBtn type="submit" disabled={saving} testId="save-hero-btn">{saving ? "Guardando..." : "Guardar banners"}</PrimaryBtn>
      </form>
    </div>
  );
}
